import { clearTokenCache, stopProactiveRefresh } from '../services/authService.js';

const deleteIndexedDbDatabase = (indexedDb, databaseName) => {
    return new Promise((resolve) => {
        if (!databaseName) {
            resolve();
            return;
        }

        try {
            const request = indexedDb.deleteDatabase(databaseName);
            request.onsuccess = () => resolve();
            request.onerror = () => resolve();
            request.onblocked = () => resolve();
        } catch {
            resolve();
        }
    });
};

const clearIndexedDb = async (browserRuntime) => {
    const indexedDb = browserRuntime.indexedDB;
    if (!indexedDb?.databases) {
        return;
    }

    try {
        const databases = await indexedDb.databases();
        await Promise.all((databases || []).map((database) => deleteIndexedDbDatabase(indexedDb, database?.name)));
    } catch {
        // Ignore browser-specific IndexedDB cleanup failures.
    }
};

const clearCacheStorage = async (browserRuntime) => {
    const cacheStorage = browserRuntime.caches;
    if (!cacheStorage?.keys) {
        return;
    }

    try {
        const keys = await cacheStorage.keys();
        await Promise.all(keys.map((key) => cacheStorage.delete(key)));
    } catch {
        // Ignore cache storage cleanup failures.
    }
};

const unregisterServiceWorkers = async (browserRuntime) => {
    const serviceWorkerContainer = browserRuntime.navigator?.serviceWorker;
    if (!serviceWorkerContainer?.getRegistrations) {
        return;
    }

    try {
        const registrations = await serviceWorkerContainer.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
    } catch {
        // Ignore service worker cleanup failures.
    }
};

const clearAccessibleCookies = (browserRuntime) => {
    const cookieSource = String(browserRuntime.document?.cookie || '');
    if (!cookieSource) {
        return;
    }

    cookieSource.split(';').forEach((cookieEntry) => {
        const cookieName = cookieEntry.split('=')[0]?.trim();
        if (!cookieName) {
            return;
        }

        browserRuntime.document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
};

export const clearApplicationData = async ({ browserRuntime = globalThis, msalInstance } = {}) => {
    clearTokenCache();
    stopProactiveRefresh();

    try {
        await msalInstance?.clearCache?.();
    } catch {
        // Ignore MSAL cache cleanup failures and continue clearing browser storage.
    }

    browserRuntime.sessionStorage?.clear?.();
    browserRuntime.localStorage?.clear?.();
    clearAccessibleCookies(browserRuntime);

    await Promise.all([
        clearCacheStorage(browserRuntime),
        clearIndexedDb(browserRuntime),
        unregisterServiceWorkers(browserRuntime)
    ]);
};

export const clearApplicationDataAndReload = async ({ browserRuntime = globalThis, msalInstance } = {}) => {
    await clearApplicationData({ browserRuntime, msalInstance });
    browserRuntime.location?.reload?.();
};